import { api } from '@invisoutlet/shared/api';
import type { BuildingSummary } from '@invisoutlet/shared/components';
import type { Building } from '@invisoutlet/shared/types';
import { useState } from 'react';

// Gemini brand gradient — blue → purple → pink (extended for animation panning)
const GEMINI_GRAD_135 = 'linear-gradient(135deg, #4285F4 0%, #9B72CB 55%, #D96570 100%)';
const GEMINI_BORDER_GRAD = 'linear-gradient(135deg, #4285F4, #9B72CB, #D96570, #9B72CB, #4285F4)';

interface AISummaryPanelProps {
  entityId: string;
  displayedBuildings: Building[];
  buildingSummaries: Record<string, BuildingSummary>;
  temperatureUnit: '°F' | '°C';
}

type Status = 'idle' | 'loading' | 'success' | 'error';

export function AISummaryPanel({
  entityId,
  displayedBuildings,
  buildingSummaries,
  temperatureUnit: _temperatureUnit,
}: AISummaryPanelProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [summary, setSummary] = useState('');
  const [generatedForKey, setGeneratedForKey] = useState('');

  const currentKey = displayedBuildings
    .map((b) => b.id)
    .sort()
    .join(',');

  const scopeChanged = status === 'success' && generatedForKey !== currentKey;

  async function generate() {
    if (displayedBuildings.length === 0) return;
    setStatus('loading');

    const buildings = displayedBuildings.map((b) => {
      const s = buildingSummaries[b.id];
      return {
        id: b.id,
        name: b.name,
        city: b.address.city,
        state: b.address.state,
        onlineDevices: s?.onlineCount ?? 0,
        totalDevices: s?.totalCount ?? 0,
        temperature: s?.tempDisplay ?? '—',
        humidity: s?.humidityDisplay ?? '—',
        lux: s?.luxDisplay ?? '—',
        occupancy: s?.occupancyDisplay ?? '—',
        outlets: s?.outletsDisplay ?? '—',
        activeAlerts: s?.activeAlertCount ?? 0,
        alertSeverity: s?.highestAlertSeverity ?? null,
      };
    });

    try {
      const res = await api.ai.buildingSummary(entityId, { buildings });
      setSummary(res.data.summary);
      setGeneratedForKey(currentKey);
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }

  return (
    <>
      <style>{`
        @keyframes gemini-border-flow {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
      <div
        className="relative mb-6 rounded-xl p-5"
        style={{
          background: `linear-gradient(var(--bg-primary), var(--bg-primary)) padding-box, ${GEMINI_BORDER_GRAD} border-box`,
          backgroundSize: '300% 300%',
          border: '1.5px solid transparent',
          animation: 'gemini-border-flow 6s ease infinite',
        }}
      >
        {/* Header */}
        <div className="mb-3 flex items-center gap-2">
          <GeminiStar animated={status === 'loading'} />
          <span className="text-sm font-semibold text-text-primary">AI Summary</span>
          <span className="ml-auto text-[10px] text-text-secondary opacity-50">
            Powered by Gemini
          </span>
        </div>

        {/* Idle */}
        {status === 'idle' && (
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-text-secondary">
              Get an AI-generated overview of your{' '}
              {displayedBuildings.length === 1
                ? 'selected building'
                : `${displayedBuildings.length} selected buildings`}
              .
            </p>
            <GeminiButton
              onClick={() => void generate()}
              disabled={displayedBuildings.length === 0}
            >
              Generate Summary
            </GeminiButton>
          </div>
        )}

        {/* Loading */}
        {status === 'loading' && <p className="text-sm text-text-secondary">Generating summary…</p>}

        {/* Success */}
        {status === 'success' && (
          <div>
            <p className="text-sm leading-relaxed text-text-primary">{summary}</p>
            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                onClick={() => void generate()}
                className="flex items-center gap-1.5 text-sm text-text-secondary underline-offset-2 hover:text-text-primary hover:underline focus:outline-none"
              >
                <GeminiStar size={12} />
                Regenerate
              </button>
              {scopeChanged && (
                <span className="text-xs font-medium" style={{ color: '#9B72CB' }}>
                  • Scope changed
                </span>
              )}
            </div>
          </div>
        )}

        {/* Error */}
        {status === 'error' && (
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-danger">Failed to generate summary.</p>
            <button
              type="button"
              onClick={() => void generate()}
              className="shrink-0 text-sm underline-offset-2 hover:underline focus:outline-none"
              style={{ color: '#4285F4' }}
            >
              Try again
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Gemini 4-pointed star ────────────────────────────────────────────────────

function GeminiStar({ size = 18, animated = false }: { size?: number; animated?: boolean }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={animated ? 'animate-pulse' : undefined}
    >
      <defs>
        <linearGradient id="gemini-star-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#4285F4" />
          <stop offset="55%" stopColor="#9B72CB" />
          <stop offset="100%" stopColor="#D96570" />
        </linearGradient>
      </defs>
      {/* Gemini 4-pointed flowing star */}
      <path
        d="M12 2 Q12.5 7.5 16 10 Q19.5 11.5 22 12 Q19.5 12.5 16 14 Q12.5 16.5 12 22 Q11.5 16.5 8 14 Q4.5 12.5 2 12 Q4.5 11.5 8 10 Q11.5 7.5 12 2 Z"
        fill="url(#gemini-star-grad)"
      />
    </svg>
  );
}

// ─── Gemini gradient pill button ──────────────────────────────────────────────

function GeminiButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="shrink-0 rounded-full px-4 py-1.5 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-40"
      style={{ background: disabled ? '#9B72CB' : GEMINI_GRAD_135 }}
    >
      {children}
    </button>
  );
}
